import { ChangeDetectionStrategy, Component, OnInit, OnDestroy, OnChanges, SimpleChanges, Input, inject, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { ProjectLockService } from '../../shared/services/project-lock/project-lock.service';
import { AppStateService } from '../../shared/services/app-state/app-state.service';
import { ProjectVersion } from '@app/shared/interfaces';

@Component({
  selector: 'app-project-lock',
  templateUrl: './project-lock.component.html',
  styleUrls: ['./project-lock.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatDividerModule,
    MatTooltipModule,
    MatCheckboxModule,
    FormsModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectLockComponent implements OnInit, OnDestroy, OnChanges {
  @Input() projectId!: number;
  @Input() projectVersions: ProjectVersion[] = [];
  @Input() selectedVersionId?: number;

  private readonly projectLockService = inject(ProjectLockService);
  private readonly appStateService = inject(AppStateService);
  private readonly toastr = inject(ToastrService);

  keepProjectLocked = false;

  // Expose service state as computed properties
  readonly isLocked = this.projectLockService.isLocked;
  readonly isCurrentEditor = this.projectLockService.isCurrentEditor;
  readonly projectLockedBy = this.projectLockService.projectLockedBy;
  readonly currentEditor = this.projectLockService.currentEditor;

  // Computed properties for UI
  readonly canPerformLockOperations = computed(() => 
    this.projectLockService.canUserPerformLockOperations()
  );
  readonly canUnlockProject = computed(() => 
    this.projectLockService.canUserUnlockProject()
  );
  readonly shouldUseForceUnlock = computed(() => 
    this.projectLockService.shouldUseForceUnlock()
  );
  readonly isHeadVersion = computed(() => {
    // If we don't have version data yet, assume we're on head version optimistically
    if (!this.selectedVersionId || !this.projectVersions || this.projectVersions.length === 0) {
      return true; // Optimistically assume head version until data loads
    }
    const currentVersion = this.projectVersions.find(v => v.versionId === this.selectedVersionId);
    return currentVersion?.majorVersion === 0 && currentVersion?.minorVersion === 0;
  });

  ngOnInit(): void {
    if (this.projectId) {
      this.projectLockService.initialize(this.projectId);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Re-initialize if projectId changes
    if (changes['projectId'] && !changes['projectId'].firstChange && this.projectId) {
      this.projectLockService.cleanup();
      this.projectLockService.initialize(this.projectId);
    }
  }

  ngOnDestroy(): void {
    // Auto-unlock project if it was auto-locked and user didn't manually interact with lock
    if (
      this.projectLockService.getWasAutoLocked() &&
      !this.keepProjectLocked &&
      this.isCurrentEditor() &&
      this.isLocked()
    ) {
      this.autoUnlockProject();
    }
    this.projectLockService.cleanup();
  }

  /**
   * Check if the currently selected version is a head version
   */
  isCurrentVersionHeadVersion(): boolean {
    return this.isHeadVersion();
  }

  /**
   * Toggle lock state
   */
  toggleLock(): void {
    // Lock/unlock only works on head version
    if (!this.isCurrentVersionHeadVersion()) {
      this.toastr.warning(
        'Lock/unlock is only available for the head version (0.0)',
        'Version Restriction'
      );
      return;
    }

    // Check permissions before allowing lock/unlock operations
    if (!this.canPerformLockOperations()) {
      this.toastr.warning(
        'You do not have permission to lock/unlock this project',
        'Permission Denied'
      );
      return;
    }

    // User manually interacted with lock button - mark as intentional (not auto-locked)
    this.projectLockService.setWasAutoLocked(false);

    this.projectLockService.toggleLock(this.projectId);
  }

  /**
   * Check if the lock button should be visible
   */
  shouldShowLockButton(): boolean {
    if (!this.isCurrentVersionHeadVersion()) {
      return false;
    }
    return !this.isLocked() && this.canPerformLockOperations();
  }

  /**
   * Check if the unlock button should be visible
   */
  shouldShowUnlockButton(): boolean {
    if (!this.isCurrentVersionHeadVersion()) {
      return false;
    }
    return this.isLocked() && this.canUnlockProject();
  }

  /**
   * Check if the lock status button should be visible
   * Always show on head version (regardless of permissions) so all users can see lock status
   * Show optimistically if version data isn't loaded yet (assume head version)
   */
  shouldShowLockStatusButton(): boolean {
    // Always show if we have a projectId - visibility is not permission-based
    // This allows all users (owner, admin, read-only, etc.) to see the lock status
    if (!this.projectId) {
      return false;
    }
    
    // If we don't have version data yet, show the button optimistically
    // Assume we're on head version until data loads
    if (!this.selectedVersionId || !this.projectVersions || this.projectVersions.length === 0) {
      return true; // Show optimistically
    }
    
    // Always show on head version (0.0) - this is the main requirement
    // Permissions only control whether lock/unlock actions are available in the menu
    return this.isCurrentVersionHeadVersion();
  }

  /**
   * Get the text to display for lock status
   */
  getLockStatusText(): string {
    if (!this.isLocked()) {
      return 'Unlocked';
    }

    // When locked, always show "Locked by" (avatar shows who)
    return 'Locked by';
  }

  /**
   * Get avatar initials for the current editor
   */
  getAvatarInitials(): string {
    const editor = this.currentEditor();
    if (!editor) return '';
    
    const name = editor.nickname || editor.username || '';
    if (!name) return '';
    
    // Get first letter of first word and first letter of last word
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name[0].toUpperCase();
  }

  /**
   * Get the tooltip text for the lock button
   */
  getLockTooltip(): string {
    if (!this.isCurrentVersionHeadVersion()) {
      return 'Lock/unlock is only available for the head version (0.0)';
    }

    if (!this.isLocked()) {
      return this.canPerformLockOperations()
        ? 'Click to lock this project for editing'
        : 'You do not have permission to lock this project';
    }

    if (this.isCurrentEditor()) {
      return 'You have locked this project. Click to unlock.';
    }

    const ownerText = this.getLockOwnerText();
    if (this.canUnlockProject()) {
      return this.shouldUseForceUnlock()
        ? `Project is locked by ${ownerText}. Click to force unlock as project owner/admin.`
        : `Project is locked by ${ownerText}. Click to unlock.`;
    }
    return `Project is locked by ${ownerText}. You cannot unlock it.`;
  }

  /**
   * Get the lock owner text
   */
  private getLockOwnerText(): string {
    return this.projectLockedBy() ?? 'another user';
  }

  /**
   * Auto-unlock project when user leaves (if it was auto-locked and keepProjectLocked is false)
   */
  private autoUnlockProject(): void {
    if (!this.canUnlockProject()) {
      return;
    }

    this.projectLockService.unlockProject(this.projectId, false).catch((error) => {
      console.warn('Failed to auto-unlock project:', error);
    });
  }
}

